import styled from '@emotion/styled'
import {
  Status1Icon,
  Status2Icon,
  Status3Icon,
} from 'components/CustomIcons/StatusIcons'
import {
  EntityChangeList,
  EntityChangeListProps,
} from 'components/EntityChangeList'
import {
  renderDate,
  renderDefault,
  renderEnum,
  renderMandatory,
} from 'components/EntityList'
import { Box } from 'grid'
import { useCurrentProving } from 'hooks/useCurrentProving'
import {
  LicensePlateType,
  ProvingType,
  TransportType,
  Vehicle,
  VehicleType,
} from 'model'
import {
  ConstrainMode,
  IColumn,
  Icon,
  mergeStyles,
  SelectionMode,
  Stack,
  TooltipHost,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import { createFieldColumn } from 'views/helpers'
import { useDoubleBookingsVehiclesListQuery } from 'views/ProvingDetailsView/hooks'

export type VehicleListProps = EntityChangeListProps<
  Partial<Vehicle> & { _id: string }
>

const StyledBox = styled(Box)`
  padding-top: 3px;
`

export const VehicleList: React.FunctionComponent<VehicleListProps> = ({
  items,
  ...props
}) => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const { data, loading } = useDoubleBookingsVehiclesListQuery()
  const result =
    !loading && data && data.cachedProvings ? data.cachedProvings : []
  const checkForDoublebooking = React.useCallback(
    (v: any) => {
      const doublebookings = [] as any[]
      const currentVehicle = v || []
      result.forEach((prov) =>
        (prov.vehicles || []).forEach((vehicle: any) => {
          if (
            vehicle &&
            currentVehicle &&
            currentVehicle.eqVehicle &&
            (vehicle.eqVehicle ? vehicle.eqVehicle.id : '') ===
              currentVehicle.eqVehicle.id
          ) {
            if (
              vehicle.dateFrom <= currentVehicle.dateTo &&
              vehicle.dateTo >= currentVehicle.dateFrom
            ) {
              doublebookings.push({
                vehicle: currentVehicle,
              })
            }
          }
        })
      )
      let count = 0
      doublebookings.forEach((test) => {
        if (
          test.vehicle &&
          test.vehicle.eqVehicle &&
          test.vehicle.eqVehicle &&
          currentVehicle.eqVehicle
        ) {
          if (test.vehicle.eqVehicle.id === currentVehicle.eqVehicle.id) {
            count++
            return count
          } else {
            return count
          }
        } else {
          return count
        }
      })
      return count
    },
    [result]
  )
  const renderModel = (item: any, i: any, col: any) => {
    if (item.type === VehicleType.External) {
      return t('enum.vehicleType.external')
    } else {
      return renderDefault(item, i, col)
    }
  }

  const renderLabel = (item: any, i: any, col: any) => {
    if (item.unknown) {
      return <Box>{t('model.proving.vehicle.vehicleUnknown.long')}</Box>
    }
    if (item.type === VehicleType.Internal) {
      // Fall back to eqVehicle.label if item.label is null/empty
      const label = item.label || (item.eqVehicle && item.eqVehicle.label) || ''
      return <Box>{label}</Box>
    } else {
      return <Box>{item.labelExternal}</Box>
    }
  }

  const renderLicensePlate = (item: Vehicle, i: any, col: any) => {
    if (
      item.licensePlateType &&
      item.licensePlateType === LicensePlateType.Unknown
    ) {
      return t('model.proving.vehicle.licensePlateUnknown')
    }
    if (
      item.licensePlateType &&
      item.licensePlateType === LicensePlateType.NoRegistration
    ) {
      return t('model.proving.vehicle.licensePlateNoRegistration')
    }
    if (item.unknown) {
      return t('model.proving.vehicle.vehicleUnknown.short')
    }
    const licensePlate = item.licensePlate || ((item as any).eqVehicle && (item as any).eqVehicle.licensePlate) || ''
    return licensePlate
  }

  const renderTransportInformation = (vehicle: Vehicle) => {
    if (vehicle.transport && vehicle.transport.transportType) {
      const outboundSender = vehicle.transport.outboundTransportSender
        ? vehicle.transport.outboundTransportSender.place
        : ''
      const outboundRecipient = vehicle.transport.outboundTransportRecipient
        ? vehicle.transport.outboundTransportRecipient.place
        : ''
      const returnSender = vehicle.transport.returnTransportSender
        ? vehicle.transport.returnTransportSender.place
        : ''
      const returnRecipient = vehicle.transport.returnTransportRecipient
        ? vehicle.transport.returnTransportRecipient.place
        : ''
      const outboundSenderTime =
        vehicle.transport.outboundTransportSenderTime || ''
      const outboundRecipientTime =
        vehicle.transport.outboundTransportRecipientTime || ''
      const returnSenderTime = vehicle.transport.returnTransportSenderTime || ''
      const returnRecipientTime =
        vehicle.transport.returnTransportRecipientTime || ''
      switch (vehicle.transport.transportType) {
        case TransportType.OutboundTransport:
          return (
            <div>
              {'Hin: '}
              <br />
              {outboundSender} {outboundSenderTime}
              {' - '}
              {outboundRecipient} {outboundRecipientTime}
            </div>
          )
        case TransportType.ReturnTransport:
          return (
            <div>
              {'Rück: '}
              <br />
              {returnSender} {returnSenderTime}
              {' - '}
              {returnRecipient} {returnRecipientTime}
            </div>
          )
        case TransportType.OutboundAndReturnTransport:
          return (
            <div>
              {'Hin: '}
              <br />
              {outboundSender} {outboundSenderTime}
              {' - '}
              {outboundRecipient} {outboundRecipientTime}
              <br />
              {'Rück: '}
              <br />
              {returnSender} {returnSenderTime}
              {' - '}
              {returnRecipient} {returnRecipientTime}
            </div>
          )
        default:
          break
      }
    }
    return <></>
  }
  const theme = useTheme()
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const iconClassRed = mergeStyles({
    color: theme.palette.red,
  })
  const columns = React.useMemo(
    () => [
      fieldColumn('model', {
        isGrouped: true,
        getGroupValue: (item: any, col: IColumn) =>
          item.type === VehicleType.Internal
            ? item.model || t('model.proving.vehicle.modelUnknown')
            : t('enum.vehicleType.external'),
        onRender: renderModel,
      }),
      fieldColumn('label', {
        onRender: (item: Vehicle, i, col) => {
          return (
            <Stack horizontal={true}>
              {checkForDoublebooking(item) > 1 && (
                <TooltipHost content={'Doppelbuchung'}>
                  <Stack.Item>
                    <StyledBox>
                      <Icon iconName="Important" className={iconClassRed} />
                    </StyledBox>
                  </Stack.Item>
                </TooltipHost>
              )}
              <Stack.Item>{renderLabel(item, i, col)}</Stack.Item>
            </Stack>
          )
        },
        ungroupable: true,
        minWidth: 130,
      }),
      fieldColumn('licensePlate', {
        onRender: renderLicensePlate,
        ungroupable: true,
      }),
      fieldColumn('dateFrom', { onRender: renderDate, ungroupable: true }),
      fieldColumn('dateTo', { onRender: renderDate, ungroupable: true }),
      fieldColumn('orderId', {
        onRender: (item: Vehicle, i, col) => {
          if (!item.orderId) {
            return renderMandatory(item, i, col)
          } else {
            return item.orderId
          }
        },
      }),
      fieldColumn('transport.transportType', {
        onRender: (item: Vehicle, i, col) => {
          if (item.transport && item.transport.bookingStatus) {
            switch (item.transport.bookingStatus) {
              case 'open':
                return (
                  <Box>
                    <Status1Icon />{' '}
                    {renderEnum(t, 'transportType')(item, i, col)}
                  </Box>
                )
              case 'workInProgress':
                return (
                  <Box>
                    <Status2Icon />{' '}
                    {renderEnum(t, 'transportType')(item, i, col)}
                  </Box>
                )
              case 'finished':
                return (
                  <Box>
                    <Status3Icon />{' '}
                    {renderEnum(t, 'transportType')(item, i, col)}
                  </Box>
                )
              default:
                return renderEnum(t, 'transportType')(item, i, col)
            }
          } else {
            return renderEnum(t, 'transportType')(item, i, col)
          }
        },
        minWidth: 120,
        ungroupable: true,
      }),
      fieldColumn('transport', {
        ungroupable: true,
        customFieldName: 'Transportinformationen',
        onRender: renderTransportInformation,
        minWidth: 240,
      }),
      fieldColumn('vin', { ungroupable: true }),
    ],
    [checkForDoublebooking, fieldColumn, t]
  )

  return (
    <EntityChangeList
      alternate={true}
      stickyHeader={true}
      selectionMode={SelectionMode.multiple}
      items={items}
      constrainMode={ConstrainMode.horizontalConstrained}
      columns={columns}
      parentGroup={
        (proving.type && proving.type === ProvingType.Block) ||
        proving.enableGroups
          ? 'group'
          : undefined
      }
      noParentGroupText={t('noGroup')}
      onShouldVirtualize={() => false}
      {...props}
    />
  )
}
